import {
  ActionWrapper,
  CardContainer,
  Details,
  ItemName,
} from 'features/menu/components/menu-reorder-widget/menu-reorder-card/styled';
import { MappedOrder } from 'features/menu/components/menu-reorder-widget/types';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { ItemThumbnailWithBadge } from 'components/item-thumbnail-with-badge/item-thumbnail-with-badge';
import { Typography } from 'components/ui';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useOrderDetailsCard } from 'hooks/use-order-details-card';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useCreateOrderAriaLabel } from 'utils/accessibility';
import { HapticsImpactStyle } from 'utils/haptic';
import { hapticImpact } from 'utils/haptic/haptic';

export const MenuReorderCard = ({
  order,
  toggleDetailsDrawer,
  drawerIsOpen,
}: {
  order: MappedOrder;
  toggleDetailsDrawer: () => void;
  drawerIsOpen: boolean;
}) => {
  const createAriaLabel = useCreateOrderAriaLabel();
  const { handleReorderClick, isReorderingDisabled } = useReorder(order);
  const isMobile = useIsMobileBreakpoint();

  const linkTextKey = drawerIsOpen ? 'hideDetails' : 'viewDetails';
  const linkText = t(linkTextKey);

  const logViewEvent = () => {
    logEvent(CustomEventNames.LAST_ORDER_HOME_VIEW, EventTypes.Other);
  };
  const logReorderEvent = () => logEvent(CustomEventNames.LAST_ORDER_REORDER, EventTypes.Other);
  const logOpenDetailEvent = () => logEvent(CustomEventNames.LAST_ORDER_DETAILS, EventTypes.Other);

  const { itemHighlighted, totalItems, itemNames } = useOrderDetailsCard(order, true);

  useEffectOnce(() => {
    logViewEvent();
  });

  if (isReorderingDisabled) {
    return null;
  }

  return (
    <CardContainer>
      {itemHighlighted && <ItemThumbnailWithBadge item={itemHighlighted} totalItems={totalItems} />}
      <Details>
        <ItemName numberOfLines={isMobile ? 2 : 1} maxFontSizeMultiplier={1.2}>
          {itemNames}
        </ItemName>
        <Typography
          variant="link"
          maxFontSizeMultiplier={1.2}
          onPress={() => {
            toggleDetailsDrawer();
            logOpenDetailEvent();
          }}
        >
          {linkText}
        </Typography>
      </Details>
      <ActionWrapper>
        <ActionButton
          testID="reorder-button"
          onPress={() => {
            if (drawerIsOpen) {
              toggleDetailsDrawer();
            }
            handleReorderClick();
            hapticImpact({ style: HapticsImpactStyle.Light });
            logReorderEvent();
          }}
          disabled={isReorderingDisabled}
          size={ActionButtonSizes.SMALL}
          accessibilityLabel={createAriaLabel({
            buttonType: 'reorder',
            order,
          })}
        >
          {t('reorder')}
        </ActionButton>
      </ActionWrapper>
    </CardContainer>
  );
};
